import type { ImgHTMLAttributes } from "react";

interface ArticleImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> {
  /** Desktop URL — used at viewports ≥768px (or everywhere if mobile is null). */
  src: string | null;
  /** Mobile URL — used at viewports <768px (or everywhere if desktop is null). */
  mobileSrc: string | null;
  sizes?: string;
}

/**
 * Renders an article thumbnail with a desktop/mobile split. Either URL may
 * be null; if both are null the component renders nothing (caller should
 * render ArticlePlaceholder instead).
 *
 *  - both set → <picture> with mobile <source> + desktop <img>
 *  - only desktop → plain <img> with desktop URL
 *  - only mobile → plain <img> with mobile URL
 */
const ArticleImage = ({ src, mobileSrc, ...imgProps }: ArticleImageProps) => {
  const effectiveSrc = src ?? mobileSrc;
  if (!effectiveSrc) return null;

  return (
    <picture>
      {src && mobileSrc && (
        <source media="(max-width: 767px)" srcSet={mobileSrc} />
      )}
      <img src={effectiveSrc} {...imgProps} />
    </picture>
  );
};

export default ArticleImage;